// src/pages/components/NotificationsTab.jsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

/**
 * Dashboard navigation:
 *
 * window.addEventListener("oppies:navigate", (e) => {
 *   // e.detail.tab
 *   // e.detail.deeplink => { postId, commentId }
 * });
 *
 * CommunityChatTab can also read and clear:
 *
 * localStorage.getItem("feed_deeplink")
 *
 * when it mounts as a fallback.
 */

const API_BASE =
    "https://oppiesfunc.azurewebsites.net/api";

/* =========================================================
   LIVE FUNCTION KEYS
========================================================= */

const KEYS = {
    listNotifications:
        "w-Y-lXOcyp978HYO4dEBZTT8dLKIX0iubhiqXjQ7Lsu_AzFueAS4wA==",

    markSaleRead:
        "9U-_Iv6msoAtbHTRmuad2p3WABfWIAeqgI3G--VxaLaYAzFumCSeYQ==",

    markFeedRead:
        "rT3AWLrstUJ6WaRDIHS8k-x300_g6C1AgJofd1HoXoOnAzFuTRvHtQ==",
};

/* =========================================================
   HELPERS
========================================================= */

const safeMs = (dateValue) => {
    const timestamp =
        Date.parse(dateValue);

    return Number.isFinite(
        timestamp
    )
        ? timestamp
        : 0;
};

const formatWhen = (
    isoOrTimestamp
) => {
    if (!isoOrTimestamp) {
        return "";
    }

    const date =
        new Date(
            isoOrTimestamp
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    return date.toLocaleString(
        "en-NZ",
        {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }
    );
};

const formatRelativeWhen = (
    isoOrTimestamp
) => {
    if (!isoOrTimestamp) {
        return "";
    }

    const date =
        new Date(
            isoOrTimestamp
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    const diffMs =
        Date.now() -
        date.getTime();

    const minute =
        60 * 1000;

    const hour =
        60 * minute;

    const day =
        24 * hour;

    if (
        diffMs >= 0 &&
        diffMs < minute
    ) {
        return "Just now";
    }

    if (
        diffMs >= minute &&
        diffMs < hour
    ) {
        const minutes =
            Math.floor(
                diffMs /
                minute
            );

        return `${minutes} ${minutes === 1
                ? "minute"
                : "minutes"
            } ago`;
    }

    if (
        diffMs >= hour &&
        diffMs < day
    ) {
        const hours =
            Math.floor(
                diffMs /
                hour
            );

        return `${hours} ${hours === 1
                ? "hour"
                : "hours"
            } ago`;
    }

    if (
        diffMs >= day &&
        diffMs <
        7 * day
    ) {
        const days =
            Math.floor(
                diffMs /
                day
            );

        return `${days} ${days === 1
                ? "day"
                : "days"
            } ago`;
    }

    return formatWhen(
        isoOrTimestamp
    );
};

/*
 * Existing sale notifications do
 * not necessarily contain kind.
 *
 * Keep the same inference logic
 * used by the existing portal.
 */

const inferKind = (
    notification
) => {
    if (
        notification?.kind
    ) {
        return notification.kind;
    }

    if (
        typeof notification
            ?.saleTitle !==
        "undefined" ||
        typeof notification
            ?.shopId !==
        "undefined" ||
        typeof notification
            ?.timestamp !==
        "undefined"
    ) {
        return "sale";
    }

    return "feed";
};

/*
 * Feed notification message.
 *
 * Actor name is deliberately NOT
 * included here because the row
 * displays actorName separately.
 *
 * This removes the previous:
 *
 * John
 * John liked your post
 *
 * duplication.
 */

const messageForFeed = (
    notification
) => {
    const subject =
        notification
            ?.subjectType ||
        (notification
            ?.commentId
            ? "comment"
            : "post");

    if (
        notification?.type ===
        "comment"
    ) {
        return subject ===
            "comment"
            ? "replied to your comment"
            : "commented on your post";
    }

    if (
        notification?.type ===
        "like" ||
        notification?.type ===
        "comment_like"
    ) {
        return subject ===
            "comment"
            ? "liked your comment"
            : "liked your post";
    }

    if (
        notification?.type ===
        "mention"
    ) {
        return subject ===
            "comment"
            ? "mentioned you in a comment"
            : "mentioned you in a post";
    }

    return `interacted with your ${subject}`;
};

const feedTypeLabel = (
    notification
) => {
    if (
        notification?.type ===
        "comment"
    ) {
        return notification
            ?.subjectType ===
            "comment"
            ? "Reply"
            : "Comment";
    }

    if (
        notification?.type ===
        "like" ||
        notification?.type ===
        "comment_like"
    ) {
        return "Like";
    }

    if (
        notification?.type ===
        "mention"
    ) {
        return "Mention";
    }

    return "Community";
};

/* =========================================================
   API HELPERS
========================================================= */

const apiGet =
    async (path) => {
        const response =
            await fetch(
                `${API_BASE}${path}`,
                {
                    method: "GET",
                }
            );

        if (!response.ok) {
            const text =
                await response
                    .text()
                    .catch(
                        () => ""
                    );

            throw new Error(
                `${response.status} ${response.statusText} — ${text}`
            );
        }

        return response.json();
    };

const apiPost =
    async (
        path,
        body
    ) => {
        const response =
            await fetch(
                `${API_BASE}${path}`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json",
                    },

                    body:
                        JSON.stringify(
                            body || {}
                        ),
                }
            );

        if (!response.ok) {
            const text =
                await response
                    .text()
                    .catch(
                        () => ""
                    );

            throw new Error(
                `${response.status} ${response.statusText} — ${text}`
            );
        }

        return response.json();
    };

/* =========================================================
   NOTIFICATION API
========================================================= */

const NotifAPI = {
    list: async ({
        email,
        limit = 100,
        cursor = "",
    }) =>
        apiGet(
            `/getNotificationsByUser?code=${KEYS.listNotifications}` +
            `&email=${encodeURIComponent(email)}` +
            `&limit=${limit}` +
            (cursor
                ? `&cursor=${encodeURIComponent(cursor)}`
                : "") +
            `&_t=${Date.now()}`
        ),

    markSaleRead:
        async ({
            email,
            shopId,
            saleTitle,
        }) =>
            apiPost(
                `/markNotificationAsRead?code=${KEYS.markSaleRead}`,
                {
                    email,
                    shopId:
                        shopId || "",
                    saleTitle:
                        saleTitle || "",
                }
            ),

    markFeedRead:
        async ({
            email,
            id,
        }) =>
            apiPost(
                `/markFeedNotificationsRead?code=${KEYS.markFeedRead}`,
                {
                    email,
                    id,
                }
            ),
};

/* =========================================================
   COMPONENT
========================================================= */

export default function NotificationsTab() {
    const currentEmail =
        useMemo(
            () =>
                localStorage.getItem(
                    "shopEmail"
                ) || "",
            []
        );

    const [rows, setRows] =
        useState([]);

    const [cursor, setCursor] =
        useState("");

    const [loading, setLoading] =
        useState(true);

    const [
        loadingMore,
        setLoadingMore,
    ] = useState(false);

    const [
        refreshing,
        setRefreshing,
    ] = useState(false);

    const [error, setError] =
        useState("");

    const [
        activeFilter,
        setActiveFilter,
    ] = useState("all");

    /* =======================================================
       HYDRATE
    ======================================================= */

    const hydrate =
        useCallback(
            (array = []) => {
                const list =
                    array.map(
                        (
                            notification,
                            index
                        ) => {
                            const kind =
                                inferKind(
                                    notification
                                );

                            const createdAt =
                                notification
                                    ?.createdAt ||
                                notification
                                    ?.timestamp ||
                                null;

                            const id =
                                notification
                                    ?.id ||
                                `${kind}_${notification?.postId || ""}_${notification?.commentId || ""}_${safeMs(createdAt)}_${index}`;

                            return {
                                ...notification,

                                id,

                                kind,

                                createdAt,

                                read:
                                    !!notification
                                        ?.read,
                            };
                        }
                    );

                return list.sort(
                    (a, b) =>
                        safeMs(
                            b.createdAt
                        ) -
                        safeMs(
                            a.createdAt
                        )
                );
            },
            []
        );

    /* =======================================================
       LOAD INITIAL / REFRESH
    ======================================================= */

    /*
     * Notice cursor is NOT a dependency
     * here.
     *
     * The old load() callback depended
     * on cursor, while the effect depended
     * on load().
     *
     * Changing cursor therefore rebuilt
     * load() and could trigger another
     * initial request.
     */

    const loadInitial =
        useCallback(
            async ({
                showRefreshState = false,
            } = {}) => {
                if (
                    !currentEmail
                ) {
                    setRows([]);
                    setCursor("");
                    setLoading(false);

                    return;
                }

                try {
                    setError("");

                    if (
                        showRefreshState
                    ) {
                        setRefreshing(
                            true
                        );
                    } else {
                        setLoading(
                            true
                        );
                    }

                    const result =
                        await NotifAPI.list(
                            {
                                email:
                                    currentEmail,

                                limit: 200,

                                cursor: "",
                            }
                        );

                    const notifications =
                        Array.isArray(
                            result
                                ?.notifications
                        )
                            ? result.notifications
                            : [];

                    setRows(
                        hydrate(
                            notifications
                        )
                    );

                    setCursor(
                        result
                            ?.nextCursor ||
                        ""
                    );
                } catch (err) {
                    console.error(
                        "Notifications load failed:",
                        err?.message ||
                        err
                    );

                    setError(
                        "We couldn't load your notifications. Please try again."
                    );
                } finally {
                    setLoading(false);

                    setRefreshing(
                        false
                    );
                }
            },
            [
                currentEmail,
                hydrate,
            ]
        );

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    /* =======================================================
       LOAD MORE
    ======================================================= */

    const loadMore =
        async () => {
            if (
                !currentEmail ||
                !cursor ||
                loadingMore
            ) {
                return;
            }

            try {
                setError("");

                setLoadingMore(
                    true
                );

                const result =
                    await NotifAPI.list(
                        {
                            email:
                                currentEmail,

                            limit: 200,

                            cursor,
                        }
                    );

                const notifications =
                    Array.isArray(
                        result
                            ?.notifications
                    )
                        ? result.notifications
                        : [];

                const newItems =
                    hydrate(
                        notifications
                    );

                setRows(
                    (previous) => {
                        /*
                         * Deduplicate using
                         * notification id.
                         */

                        const map =
                            new Map();

                        [
                            ...previous,
                            ...newItems,
                        ].forEach(
                            (item) => {
                                map.set(
                                    item.id,
                                    item
                                );
                            }
                        );

                        return hydrate(
                            Array.from(
                                map.values()
                            )
                        );
                    }
                );

                setCursor(
                    result
                        ?.nextCursor ||
                    ""
                );
            } catch (err) {
                console.error(
                    "Load more notifications failed:",
                    err?.message ||
                    err
                );

                setError(
                    "We couldn't load more notifications."
                );
            } finally {
                setLoadingMore(
                    false
                );
            }
        };

    /* =======================================================
       COUNTS
    ======================================================= */

    const unreadCount =
        useMemo(
            () =>
                rows.filter(
                    (item) =>
                        !item.read
                ).length,
            [rows]
        );

    const communityCount =
        useMemo(
            () =>
                rows.filter(
                    (item) =>
                        item.kind ===
                        "feed"
                ).length,
            [rows]
        );

    const saleCount =
        useMemo(
            () =>
                rows.filter(
                    (item) =>
                        item.kind ===
                        "sale"
                ).length,
            [rows]
        );

    /* =======================================================
       FILTER
    ======================================================= */

    const filteredRows =
        useMemo(() => {
            switch (
            activeFilter
            ) {
                case "unread":
                    return rows.filter(
                        (item) =>
                            !item.read
                    );

                case "feed":
                    return rows.filter(
                        (item) =>
                            item.kind ===
                            "feed"
                    );

                case "sale":
                    return rows.filter(
                        (item) =>
                            item.kind ===
                            "sale"
                    );

                default:
                    return rows;
            }
        }, [
            rows,
            activeFilter,
        ]);

    /* =======================================================
       OPTIMISTIC READ
    ======================================================= */

    const markLocalRead =
        useCallback((id) => {
            setRows(
                (previous) =>
                    previous.map(
                        (row) =>
                            row.id === id
                                ? {
                                    ...row,
                                    read: true,
                                }
                                : row
                    )
            );
        }, []);

    /* =======================================================
       OPEN SALE
    ======================================================= */

    const openSale =
        useCallback(
            async (item) => {
                markLocalRead(
                    item.id
                );

                try {
                    await NotifAPI.markSaleRead(
                        {
                            email:
                                currentEmail,

                            shopId:
                                item.shopId ||
                                "",

                            saleTitle:
                                item.saleTitle ||
                                "",
                        }
                    );
                } catch (err) {
                    /*
                     * Optimistic local read
                     * remains in place.
                     */

                    console.warn(
                        "mark sale read failed:",
                        err?.message ||
                        err
                    );
                }

                /*
                 * There is currently no
                 * sale-details destination
                 * in this component.
                 *
                 * Preserve existing behaviour:
                 * clicking marks it read.
                 */
            },
            [
                currentEmail,
                markLocalRead,
            ]
        );

    /* =======================================================
       OPEN COMMUNITY NOTIFICATION
    ======================================================= */

    const openFeed =
        useCallback(
            async (item) => {
                markLocalRead(
                    item.id
                );

                /*
                 * Best-effort backend
                 * update. Navigation should
                 * not be blocked by it.
                 */

                NotifAPI.markFeedRead(
                    {
                        email:
                            currentEmail,

                        id: item.id,
                    }
                ).catch(
                    (err) => {
                        console.warn(
                            "mark feed read failed:",
                            err?.message ||
                            err
                        );
                    }
                );

                const deeplink = {
                    postId:
                        item.postId ||
                        null,

                    commentId:
                        item.commentId ||
                        null,
                };

                /*
                 * Backwards-compatible
                 * localStorage fallback.
                 */

                localStorage.setItem(
                    "feed_deeplink",
                    JSON.stringify({
                        ...deeplink,

                        fromNotification:
                            true,

                        highlightCommentId:
                            item.commentId ||
                            null,
                    })
                );

                /*
                 * Primary portal navigation.
                 */

                window.dispatchEvent(
                    new CustomEvent(
                        "oppies:navigate",
                        {
                            detail: {
                                tab:
                                    "Community Chat",

                                deeplink,
                            },
                        }
                    )
                );
            },
            [
                currentEmail,
                markLocalRead,
            ]
        );

    /* =======================================================
       ICON
    ======================================================= */

    const renderNotificationIcon = (
        item
    ) => {
        if (
            item.kind ===
            "sale"
        ) {
            return (
                <svg
                    viewBox="0 0 24 24"
                    width="19"
                    height="19"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M20.59 13.41 11 3.83V3H4v7h.83l9.58 9.59a2 2 0 0 0 2.82 0l3.36-3.36a2 2 0 0 0 0-2.82Z" />

                    <circle
                        cx="7.5"
                        cy="6.5"
                        r=".8"
                        fill="currentColor"
                        stroke="none"
                    />
                </svg>
            );
        }

        if (
            item.type ===
            "like" ||
            item.type ===
            "comment_like"
        ) {
            return (
                <svg
                    viewBox="0 0 24 24"
                    width="19"
                    height="19"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
                </svg>
            );
        }

        if (
            item.type ===
            "mention"
        ) {
            return (
                <svg
                    viewBox="0 0 24 24"
                    width="19"
                    height="19"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle
                        cx="12"
                        cy="12"
                        r="4"
                    />

                    <path d="M16 12v1.5a2.5 2.5 0 0 0 5 0V12a9 9 0 1 0-3.7 7.3" />
                </svg>
            );
        }

        return (
            <svg
                viewBox="0 0 24 24"
                width="19"
                height="19"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
            </svg>
        );
    };

    /* =======================================================
       ROW
    ======================================================= */

    const renderRow = (
        item
    ) => {
        const isSale =
            item.kind ===
            "sale";

        const actor =
            isSale
                ? item.shopId ||
                "OPPIES"
                : item.actorName ||
                "Someone";

        const message =
            isSale
                ? item.saleTitle ||
                "Sale event"
                : messageForFeed(
                    item
                );

        const label =
            isSale
                ? "Sale"
                : feedTypeLabel(
                    item
                );

        const when =
            formatRelativeWhen(
                item.createdAt ||
                item.timestamp
            );

        const fullWhen =
            formatWhen(
                item.createdAt ||
                item.timestamp
            );

        return (
            <button
                key={item.id}
                type="button"
                className={`notification-card ${!item.read
                        ? "notification-card-unread"
                        : ""
                    }`}
                onClick={() =>
                    isSale
                        ? openSale(
                            item
                        )
                        : openFeed(
                            item
                        )
                }
            >
                <div
                    className={`notification-icon ${isSale
                            ? "notification-icon-sale"
                            : "notification-icon-feed"
                        }`}
                >
                    {renderNotificationIcon(
                        item
                    )}
                </div>

                <div className="notification-copy">

                    <div className="notification-topline">

                        <strong>
                            {actor}
                        </strong>

                        <span
                            className={`notification-type notification-type-${isSale ? "sale" : "feed"}`}
                        >
                            {label}
                        </span>

                    </div>

                    <div className="notification-message">
                        {message}
                    </div>

                    {when ? (
                        <div
                            className="notification-time"
                            title={
                                fullWhen
                            }
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="11"
                                height="11"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="9"
                                />

                                <path d="M12 7v5l3 2" />
                            </svg>

                            {when}
                        </div>
                    ) : null}

                </div>

                <div className="notification-right">

                    {!item.read ? (
                        <span className="notification-unread-dot" />
                    ) : null}

                    <svg
                        className="notification-chevron"
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="m9 18 6-6-6-6" />
                    </svg>

                </div>
            </button>
        );
    };

    /* =======================================================
       RENDER
    ======================================================= */

    return (
        <>
            <style>
                {notificationStyles}
            </style>

            <div className="notifications-page">

                {/* ================================================
            INTRO
        ================================================ */}

                <section className="notifications-intro">

                    <div>

                        <div className="notifications-eyebrow">
                            Notifications
                        </div>

                        <h1>
                            Stay up to date
                        </h1>

                        <p>
                            See activity from the
                            OPPIES community and
                            updates connected to your
                            shop.
                        </p>

                    </div>

                    <button
                        type="button"
                        className="notifications-refresh"
                        onClick={() =>
                            loadInitial({
                                showRefreshState:
                                    true,
                            })
                        }
                        disabled={
                            refreshing ||
                            loading
                        }
                    >
                        <svg
                            className={
                                refreshing
                                    ? "notifications-refresh-spinning"
                                    : ""
                            }
                            viewBox="0 0 24 24"
                            width="15"
                            height="15"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M20 6v5h-5" />

                            <path d="M4 18v-5h5" />

                            <path d="M18.5 9a7 7 0 0 0-11.8-2.5L4 9" />

                            <path d="M5.5 15a7 7 0 0 0 11.8 2.5L20 15" />
                        </svg>

                        {refreshing
                            ? "Refreshing..."
                            : "Refresh"}

                    </button>

                </section>

                {/* ================================================
            SUMMARY
        ================================================ */}

                <section className="notifications-summary">

                    <div className="notifications-summary-card">

                        <div className="notifications-summary-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />

                                <path d="M10 21h4" />
                            </svg>

                        </div>

                        <div>
                            <strong>
                                {rows.length}
                            </strong>

                            <span>
                                Total
                            </span>
                        </div>

                    </div>

                    <div className="notifications-summary-card notifications-summary-card-highlight">

                        <div className="notifications-summary-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="9"
                                />

                                <path d="M12 8v4" />

                                <path d="M12 16h.01" />
                            </svg>

                        </div>

                        <div>
                            <strong>
                                {unreadCount}
                            </strong>

                            <span>
                                Unread
                            </span>
                        </div>

                    </div>

                    <div className="notifications-summary-card">

                        <div className="notifications-summary-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
                            </svg>

                        </div>

                        <div>
                            <strong>
                                {communityCount}
                            </strong>

                            <span>
                                Community
                            </span>
                        </div>

                    </div>

                    <div className="notifications-summary-card">

                        <div className="notifications-summary-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M20.59 13.41 11 3.83V3H4v7h.83l9.58 9.59a2 2 0 0 0 2.82 0l3.36-3.36a2 2 0 0 0 0-2.82Z" />
                            </svg>

                        </div>

                        <div>
                            <strong>
                                {saleCount}
                            </strong>

                            <span>
                                Sales
                            </span>
                        </div>

                    </div>

                </section>

                {/* ================================================
            MAIN PANEL
        ================================================ */}

                <section className="notifications-panel">

                    <div className="notifications-panel-header">

                        <div>

                            <div className="notifications-panel-eyebrow">
                                Activity
                            </div>

                            <h2>
                                Recent notifications
                            </h2>

                        </div>

                        {unreadCount >
                            0 ? (
                            <div className="notifications-unread-badge">
                                {unreadCount}
                                {" unread"}
                            </div>
                        ) : null}

                    </div>

                    {/* ==============================================
              FILTERS
          ============================================== */}

                    <div className="notifications-filters">

                        <button
                            type="button"
                            className={
                                activeFilter ===
                                    "all"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setActiveFilter(
                                    "all"
                                )
                            }
                        >
                            All

                            <span>
                                {rows.length}
                            </span>
                        </button>

                        <button
                            type="button"
                            className={
                                activeFilter ===
                                    "unread"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setActiveFilter(
                                    "unread"
                                )
                            }
                        >
                            Unread

                            <span>
                                {unreadCount}
                            </span>
                        </button>

                        <button
                            type="button"
                            className={
                                activeFilter ===
                                    "feed"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setActiveFilter(
                                    "feed"
                                )
                            }
                        >
                            Community

                            <span>
                                {communityCount}
                            </span>
                        </button>

                        <button
                            type="button"
                            className={
                                activeFilter ===
                                    "sale"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setActiveFilter(
                                    "sale"
                                )
                            }
                        >
                            Sales

                            <span>
                                {saleCount}
                            </span>
                        </button>

                    </div>

                    {/* ==============================================
              ERROR
          ============================================== */}

                    {error ? (
                        <div className="notifications-error">

                            <div>
                                !
                            </div>

                            <span>
                                {error}
                            </span>

                            <button
                                type="button"
                                onClick={() =>
                                    loadInitial()
                                }
                            >
                                Try Again
                            </button>

                        </div>
                    ) : null}

                    {/* ==============================================
              LOADING
          ============================================== */}

                    {loading &&
                        rows.length ===
                        0 ? (
                        <div className="notifications-loading">

                            <div className="notifications-spinner" />

                            <strong>
                                Loading notifications
                            </strong>

                            <span>
                                Checking your latest
                                OPPIES activity...
                            </span>

                        </div>
                    ) : filteredRows.length ===
                        0 ? (
                        /* ============================================
                           EMPTY
                        ============================================ */

                        <div className="notifications-empty">

                            <div className="notifications-empty-icon">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="27"
                                    height="27"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />

                                    <path d="M10 21h4" />
                                </svg>

                            </div>

                            <strong>
                                {activeFilter ===
                                    "unread"
                                    ? "You're all caught up"
                                    : activeFilter ===
                                        "feed"
                                        ? "No community activity yet"
                                        : activeFilter ===
                                            "sale"
                                            ? "No sale notifications yet"
                                            : "No notifications yet"}
                            </strong>

                            <span>
                                {activeFilter ===
                                    "unread"
                                    ? "You don't have any unread notifications."
                                    : "New notifications will appear here when there's activity."}
                            </span>

                        </div>
                    ) : (
                        /* ============================================
                           LIST
                        ============================================ */

                        <div className="notifications-list">

                            {filteredRows.map(
                                renderRow
                            )}

                        </div>
                    )}

                    {/* ==============================================
              LOAD MORE
          ============================================== */}

                    {cursor ? (
                        <div className="notifications-more-wrap">

                            <button
                                type="button"
                                className="notifications-more"
                                onClick={
                                    loadMore
                                }
                                disabled={
                                    loadingMore
                                }
                            >
                                {loadingMore ? (
                                    <>
                                        <span className="notifications-more-spinner" />

                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Load More

                                        <svg
                                            viewBox="0 0 24 24"
                                            width="13"
                                            height="13"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="m6 9 6 6 6-6" />
                                        </svg>
                                    </>
                                )}

                            </button>

                        </div>
                    ) : null}

                </section>

            </div>
        </>
    );
}

/* =========================================================
   STYLES
========================================================= */

const notificationStyles = `

  .notifications-page {
    color: #1f2937;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  /* ======================================================
     INTRO
  ====================================================== */

  .notifications-intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 23px;
  }

  .notifications-eyebrow,
  .notifications-panel-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .notifications-intro h1 {
    margin: 7px 0;
    color: #17211c;
    font-size:
      clamp(
        26px,
        3vw,
        36px
      );
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .notifications-intro p {
    max-width: 650px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }

  .notifications-refresh {
    display: inline-flex;
    min-height: 37px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px 12px;
    border: 1px solid #d6dfdb;
    border-radius: 10px;
    background: #ffffff;
    color: #047857;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .notifications-refresh:hover:not(:disabled) {
    background: #f0fdf4;
  }

  .notifications-refresh:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .notifications-refresh-spinning {
    animation:
      notification-spin
      0.8s
      linear
      infinite;
  }

  /* ======================================================
     SUMMARY
  ====================================================== */

  .notifications-summary {
    display: grid;
    grid-template-columns:
      repeat(
        4,
        minmax(0, 1fr)
      );
    gap: 10px;
    margin-bottom: 17px;
  }

  .notifications-summary-card {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 66px;
    box-sizing: border-box;
    padding: 11px 13px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #ffffff;
  }

  .notifications-summary-card-highlight {
    border-color: #c3e7da;
    background: #f5fdf9;
  }

  .notifications-summary-icon {
    display: grid;
    width: 34px;
    height: 34px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 10px;
    background: #ecfdf5;
    color: #047857;
  }

  .notifications-summary-card strong {
    display: block;
    color: #17211c;
    font-size: 17px;
    line-height: 1;
  }

  .notifications-summary-card span {
    display: block;
    margin-top: 4px;
    color: #7b8982;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  /* ======================================================
     PANEL
  ====================================================== */

  .notifications-panel {
    padding: 21px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
      0 8px 24px
      rgba(
        15,
        23,
        42,
        0.035
      );
  }

  .notifications-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    margin-bottom: 15px;
  }

  .notifications-panel-header h2 {
    margin: 4px 0 0;
    color: #17211c;
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  .notifications-unread-badge {
    padding: 6px 9px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #047857;
    font-size: 8px;
    font-weight: 800;
  }

  /* ======================================================
     FILTERS
  ====================================================== */

  .notifications-filters {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    margin-bottom: 16px;
    padding-bottom: 2px;
  }

  .notifications-filters button {
    display: inline-flex;
    min-height: 32px;
    flex: 0 0 auto;
    align-items: center;
    gap: 7px;
    padding: 6px 10px;
    border: 1px solid #e1e6e3;
    border-radius: 9px;
    background: #ffffff;
    color: #64748b;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .notifications-filters button:hover {
    background: #f8faf9;
  }

  .notifications-filters button.active {
    border-color: #047857;
    background: #047857;
    color: #ffffff;
  }

  .notifications-filters button span {
    display: grid;
    min-width: 18px;
    height: 18px;
    box-sizing: border-box;
    place-items: center;
    padding: 0 4px;
    border-radius: 999px;
    background: #f1f5f9;
    color: #64748b;
    font-size: 7px;
  }

  .notifications-filters button.active span {
    background:
      rgba(
        255,
        255,
        255,
        0.16
      );
    color: #ffffff;
  }

  /* ======================================================
     ERROR
  ====================================================== */

  .notifications-error {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 13px;
    padding: 10px 11px;
    border: 1px solid #fecaca;
    border-radius: 11px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 9px;
    font-weight: 600;
  }

  .notifications-error > div {
    display: grid;
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50%;
    background: #fee2e2;
    font-weight: 900;
  }

  .notifications-error span {
    flex: 1;
  }

  .notifications-error button {
    padding: 6px 8px;
    border: 1px solid #efb1b1;
    border-radius: 7px;
    background: #ffffff;
    color: #b91c1c;
    cursor: pointer;
    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
  }

  /* ======================================================
     NOTIFICATION LIST
  ====================================================== */

  .notifications-list {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .notification-card {
    display: flex;
    width: 100%;
    box-sizing: border-box;
    align-items: center;
    gap: 11px;
    padding: 11px 12px;
    border: 1px solid #e9eeeb;
    border-radius: 13px;
    background: #ffffff;
    color: inherit;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition:
      border-color 0.18s ease,
      background 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;
  }

  .notification-card:hover {
    border-color: #c5ddd4;
    background: #fcfffd;
    transform: translateY(-1px);
    box-shadow:
      0 5px 14px
      rgba(
        15,
        23,
        42,
        0.04
      );
  }

  .notification-card-unread {
    border-color: #c5e4d8;
    background: #f7fdf9;
  }

  .notification-icon {
    display: grid;
    width: 37px;
    height: 37px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 11px;
  }

  .notification-icon-feed {
    background: #ecfdf5;
    color: #047857;
  }

  .notification-icon-sale {
    background: #fbf7ed;
    color: #8a6b32;
  }

  .notification-copy {
    min-width: 0;
    flex: 1;
  }

  .notification-topline {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .notification-topline strong {
    overflow: hidden;
    color: #26362e;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .notification-type {
    flex: 0 0 auto;
    padding: 3px 6px;
    border-radius: 999px;
    font-size: 6px;
    font-weight: 900;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .notification-type-feed {
    background: #ecfdf5;
    color: #047857;
  }

  .notification-type-sale {
    background: #fbf7ed;
    color: #83652f;
  }

  .notification-message {
    margin-top: 3px;
    color: #64748b;
    font-size: 9px;
    line-height: 1.45;
  }

  .notification-time {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    color: #9aa5a0;
    font-size: 7px;
  }

  .notification-right {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
  }

  .notification-unread-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #10b981;
    box-shadow:
      0 0 0 3px
      rgba(
        16,
        185,
        129,
        0.1
      );
  }

  .notification-chevron {
    color: #b1bbb6;
  }

  /* ======================================================
     LOADING / EMPTY
  ====================================================== */

  .notifications-loading,
  .notifications-empty {
    display: flex;
    min-height: 260px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 25px;
    text-align: center;
  }

  .notifications-spinner {
    width: 28px;
    height: 28px;
    margin-bottom: 11px;
    border: 3px solid #d1fae5;
    border-top-color: #047857;
    border-radius: 50%;
    animation:
      notification-spin
      0.8s
      linear
      infinite;
  }

  .notifications-loading strong,
  .notifications-empty strong {
    color: #52605a;
    font-size: 10px;
  }

  .notifications-loading span,
  .notifications-empty span {
    max-width: 260px;
    margin-top: 4px;
    color: #94a3b8;
    font-size: 8px;
    line-height: 1.5;
  }

  .notifications-empty-icon {
    display: grid;
    width: 53px;
    height: 53px;
    margin-bottom: 11px;
    place-items: center;
    border-radius: 15px;
    background: #f0f5f2;
    color: #8aa098;
  }

  /* ======================================================
     LOAD MORE
  ====================================================== */

  .notifications-more-wrap {
    display: flex;
    justify-content: center;
    margin-top: 15px;
    padding-top: 14px;
    border-top: 1px solid #eef2f0;
  }

  .notifications-more {
    display: inline-flex;
    min-height: 34px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 7px 11px;
    border: 1px solid #c8d9d2;
    border-radius: 9px;
    background: #ffffff;
    color: #047857;
    cursor: pointer;
    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
  }

  .notifications-more:hover:not(:disabled) {
    background: #f0fdf4;
  }

  .notifications-more:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .notifications-more-spinner {
    width: 9px;
    height: 9px;
    border: 2px solid #c6e8db;
    border-top-color: #047857;
    border-radius: 50%;
    animation:
      notification-spin
      0.7s
      linear
      infinite;
  }

  @keyframes notification-spin {
    to {
      transform:
        rotate(360deg);
    }
  }

  /* ======================================================
     RESPONSIVE
  ====================================================== */

  @media (max-width: 760px) {

    .notifications-summary {
      grid-template-columns:
        repeat(
          2,
          minmax(0, 1fr)
        );
    }

  }

  @media (max-width: 560px) {

    .notifications-intro {
      align-items: flex-start;
      flex-direction: column;
    }

    .notifications-panel {
      padding: 16px;
      border-radius: 17px;
    }

    .notifications-refresh {
      align-self: flex-start;
    }

  }

  @media (max-width: 390px) {

    .notifications-summary {
      grid-template-columns: 1fr;
    }

  }

`;